// Vue & Plugins
import { MaybeRefOrGetter, toValue } from 'vue'

// Composables
import { useQuery, keepPreviousData, infiniteQueryOptions, useInfiniteQuery, InfiniteData, UndefinedInitialDataInfiniteOptions, useQueryClient, useMutation, queryOptions } from '@tanstack/vue-query'

// Models
import { type SiteID } from '../../sites/models/site'
import { Pagination } from '../../common/models/pagination'
import { Group, GroupID, GroupUpdateForm } from '../models/group'

// Services
import { GroupSearchForm, archive, fetch, fetchByEmployee, restore, show, update } from '../groupService'
import { useEventStore } from '../../common/eventStore'
import { EmployeeID } from '../../employees/models/employee'

interface GroupFilters {
    name?: MaybeRefOrGetter<string>
    perPage?: MaybeRefOrGetter<number>
    page?: MaybeRefOrGetter<number>
}

export const groupKeys = {
    all: ['groups'] as const,
    lists: () => [...groupKeys.all, 'list'] as const,
    list: (filters: GroupFilters) => [...groupKeys.lists(), { name: filters.name, perPage: filters.perPage, page: filters.page }] as const,
    details: () => [...groupKeys.all, 'detail'] as const,
    detail: (id: MaybeRefOrGetter) => [...groupKeys.details(), id] as const,
    listByEmployee: (employeeId: MaybeRefOrGetter<EmployeeID>, filters: GroupFilters) => [...groupKeys.lists(), employeeId, { name: filters.name, perPage: filters.perPage, page: filters.page }] as const,
}

export function useGroupQuery(siteId: MaybeRefOrGetter<SiteID>, filters: GroupFilters = {}) {
    return useQuery<{ groups: Group[], pagination: Pagination }, Error>({
        queryKey: groupKeys.list(filters),
        queryFn: async () => {
            const form: GroupSearchForm = {
                perPage: filters.perPage ? toValue(filters.perPage) : 15,
                page: filters.page ? toValue(filters.page) : 1,
            }

            const name = toValue(filters.name)
            if (name) {
                form.name = name
            }

            return fetch(toValue(siteId), form)
        },
        placeholderData: keepPreviousData,
    })
}

type GroupQueryFnData = {
    groups: Group[]
    pagination: Pagination
}
type GroupInfiniteData = InfiniteData<GroupQueryFnData, number>
type GroupQueryKey = readonly ['groups', 'list', {
    readonly name: MaybeRefOrGetter<string> | undefined
    readonly perPage: MaybeRefOrGetter<number> | undefined
    readonly page: MaybeRefOrGetter<number> | undefined
}]
export function groupOptionsInfinite(siteId: MaybeRefOrGetter<SiteID>, filters: GroupFilters = {}): UndefinedInitialDataInfiniteOptions<GroupQueryFnData, Error, GroupInfiniteData, GroupQueryKey, number> {
    return infiniteQueryOptions({
        queryKey: groupKeys.list(filters),
        queryFn: async ({ pageParam }) => {
            const form: GroupSearchForm = {
                perPage: 15,
                page: pageParam,
            }

            const name = toValue(filters.name)
            if (name) {
                form.name = name
            }

            return await fetch(toValue(siteId), form)
        },
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.pagination.currentPage + 1

            if (nextPage > lastPage.pagination.totalPages) {
                return undefined
            }

            return nextPage
        },
    })
}

export function useGroupQueryInfinite(siteId: MaybeRefOrGetter<SiteID>, filters: GroupFilters = {}) {
    return useInfiniteQuery(groupOptionsInfinite(siteId, filters))
}

export function groupDetailOptions(siteId: MaybeRefOrGetter<SiteID>, groupId: MaybeRefOrGetter<GroupID>) {
    return queryOptions({
        queryKey: groupKeys.detail(groupId),
        queryFn: async () => {
            return show(toValue(siteId), toValue(groupId))
        },
    })
}

export function useGroupDetailQuery(siteId: MaybeRefOrGetter<SiteID>, groupId: MaybeRefOrGetter<GroupID>) {
    return useQuery(groupDetailOptions(siteId, groupId))
}

export function useGroupUpdateMutation(siteId: MaybeRefOrGetter<SiteID>, groupId: MaybeRefOrGetter<GroupID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<{ group: Group }, Error, { form: GroupUpdateForm }>({
        mutationFn: async (data) => {
            return update(toValue(siteId), toValue(groupId), data.form)
        },
        onSettled: async () => {
            return await queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
        },
        onSuccess: async () => {
            eventStore.addUpdateToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message})
        },
    })
}

export function useGroupArchiveMutation(siteId: MaybeRefOrGetter<SiteID>, groupId: MaybeRefOrGetter<GroupID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error>({
        mutationFn: async () => {
            return archive(toValue(siteId), toValue(groupId))
        },
        onSettled: async () => {
            return await queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
        },
        onSuccess: async () => {
            eventStore.addArchiveToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message})
        },
    })
}

export function useGroupRestoreMutation(siteId: MaybeRefOrGetter<SiteID>, groupId: MaybeRefOrGetter<GroupID>) {
    const queryClient = useQueryClient()
    const eventStore = useEventStore()

    return useMutation<void, Error>({
        mutationFn: async () => {
            return restore(toValue(siteId), toValue(groupId))
        },
        onSettled: async () => {
            return await queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
        },
        onSuccess: async () => {
            eventStore.addRestoreToast()
        },
        onError: async (error) => {
            eventStore.addErrorToast({ detail: error.message})
        },
    })
}

export function useGroupByEmployeeQuery(siteId: MaybeRefOrGetter<SiteID>, employeeId: MaybeRefOrGetter<EmployeeID>, filters: GroupFilters = {}) {
    return useQuery<{ groups: Group[], pagination: Pagination }, Error>({
        queryKey: groupKeys.listByEmployee(employeeId, filters),
        queryFn: async () => {
            const form: GroupSearchForm = {
                perPage: filters.perPage ? toValue(filters.perPage) : 15,
                page: filters.page ? toValue(filters.page) : 1,
            }

            const name = toValue(filters.name)
            if (name) {
                form.name = name
            }

            return fetchByEmployee(toValue(siteId), toValue(employeeId), form)
        },
        placeholderData: keepPreviousData,
    })
}